const RELOAD_STORAGE_KEY = 'chunk-reload-timestamp'
const RELOAD_TARGET_KEY = 'chunk-reload-target'
const RELOAD_COOLDOWN_MS = 30_000

/** In-memory target set by the router's beforeEach guard */
let pendingNavigationPath: string | null = null

/**
 * Detects chunk/module load errors across Chrome, Firefox, and Safari.
 * These occur when a deployment invalidates cached chunk URLs.
 */
export function isChunkLoadError(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false
    }

    const message = error.message.toLowerCase()

    return (
        // Chrome / Edge
        message.includes('failed to fetch dynamically imported module')
        // Firefox
        || message.includes('error loading dynamically imported module')
        // Safari
        || message.includes('importing a module script failed')
        // Generic chunk load failure
        || message.includes('loading chunk')
        || message.includes('loading css chunk')
    )
}

/**
 * Returns true if we already auto-reloaded within the cooldown window,
 * preventing infinite reload loops.
 */
export function hasRecentChunkReload(): boolean {
    const timestamp = sessionStorage.getItem(RELOAD_STORAGE_KEY)
    if (timestamp === null) {
        return false
    }

    return Date.now() - Number(timestamp) < RELOAD_COOLDOWN_MS
}

/**
 * Called by the router's beforeEach/afterEach to track where the user
 * is navigating, so error handlers can save it before reloading.
 */
export function setPendingNavigationPath(path: string | null): void {
    pendingNavigationPath = path
}

/**
 * Records the current timestamp, saves the target path the user was
 * navigating to (explicit or from the pending navigation), and reloads.
 */
export function reloadForChunkError(targetPath?: string): void {
    sessionStorage.setItem(RELOAD_STORAGE_KEY, String(Date.now()))

    const target = targetPath ?? pendingNavigationPath
    if (target) {
        sessionStorage.setItem(RELOAD_TARGET_KEY, target)
    }

    window.location.reload()
}

/**
 * Returns and clears the saved navigation target from a chunk-reload,
 * so the user lands on the page they originally clicked.
 */
export function consumeChunkReloadTarget(): string | null {
    const target = sessionStorage.getItem(RELOAD_TARGET_KEY)
    sessionStorage.removeItem(RELOAD_TARGET_KEY)
    return target
}

/**
 * Catches chunk load errors that occur outside of router navigation —
 * e.g. defineAsyncComponent loading after the route has already resolved.
 * These throw unhandled promise rejections that router.onError never sees.
 *
 * Uses the same cooldown logic as the router handler to prevent reload loops.
 */
export function registerGlobalChunkErrorHandler(): void {
    window.addEventListener('unhandledrejection', (event) => {
        if (!isChunkLoadError(event.reason)) {
            return
        }

        if (!hasRecentChunkReload()) {
            event.preventDefault()
            reloadForChunkError()
        }
    })
}
